import { computed, defineComponent, PropType, ref } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapActions, mapState } from "pinia";
import moment from "moment";

import { useApp } from "../../../../App/useApp";
import { useEditorStore } from "../../../store/useEditorStore";
import { useConversationStore } from "../../../store/useConversationStore";
import type {
    IAsset,
    IAssetDetails,
    IConversationUser,
    IEditMessageQuery,
    IMessageItem,
    IMessagePlaceholder,
    IReplyToMessage
} from "../../../../../resources-vue/vue-interfaces/messaging-service/i-message";
import { IPromptOptions } from "../../../../../resources-vue/vue-interfaces/i-prompt";
import {
    formatMessageTime,
    formatTaggedUserMessage,
    sortPlaceholdersList,
    truncateString
} from "../../../../../common/vue-helpers/modifiers/format-message";
import { markdownToHtml } from "../../../../../common/vue-helpers/modifiers/markdown-to-html-modifier";
import { sanitizeMarkup } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import eventBus from "../../../../../utilities/eventBus";
import { MessageService } from "../../../../../services/message-service";
import { helperTestingAgency } from "../../../../../common/vue-helpers/testing-agency-check";
import config from "../../../../../common/config";
import {
    ParentPermissionEnum,
    PermissionActionEnum,
    PlaceholdersListEnum,
    ApplicationEnum,
    ConversationTypeEnum
} from "../../../../../enums/enums";
import { NavigationService } from "../../../../../services/navigation.service";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { getHospiceRedirectionUrl } from "../../../../../common/get-hospice-redirection-url";

import MessageInputHandler from "../../../../../resources-vue/vue-custom-elements/MessagingService/MessageInputHandler/MessageInputHandler.vue";
import Alert from "../../../../../resources-vue/vue-dialogs/Alert/Alert.vue";
import Prompt from "../../../../../resources-vue/vue-dialogs/prompt/prompt.vue";
import MessageReactions from "../MessageReactions/MessageReaction.vue";
import MessageHoverActions from "../MessageHoverActions/MessageHoverActions.vue";
import EmojiPicker from "../../../../../resources-vue/vue-custom-elements/EmojiPicker/EmojiPicker.vue";
export default defineComponent({
    components: {
        MessageInputHandler,
        MessageReactions,
        MessageHoverActions,
        EmojiPicker
    },
    props: {
        message: { type: Object as PropType<IMessageItem>, default: {} as IMessageItem },
        previousMessage: {
            type: Object as PropType<IMessageItem>,
            default: null
        },
        isLastMessage: {
            type: Boolean,
            default: false
        },
        isGroupConversation: {
            type: Boolean,
            default: false
        }
    },
    emits: ["IsReplyMessage"],
    async mounted() {
        document.addEventListener("click", this.handleOutsideReadClick);
        if (this.message.assetIds.length > 0) {
            await this.renderAsset(this.activeConversation.id, this.message.id, this.message.assetIds);
        }
        const { taggedUsers, sharedProfiles } = sortPlaceholdersList(this.message?.placeHoldersList);
        this.taggedUsersList = taggedUsers;
        this.sharedProfilesList = sharedProfiles;
        if (this.message?.replyToMessage) {
            this.setRepliedMsgProfiles();
        }
        if (this.message.isDeprecated) {
            this.findUndoButtonTime();
        }
    },
    async created() {
        this._messageService = Container.instance.get(MessageService);
        this._navigationService = Container.instance.get(NavigationService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this.isImportant = this.message.isImportant;
        this.hasTestAgency = await helperTestingAgency.checkHasTestingAgency();
    },

    beforeUnmount() {
        document.removeEventListener("click", this.handleOutsideReadClick);
    },

    watch: {
        message: {
            async handler(newValue, oldValue) {
                if (newValue.isDeprecated) {
                    this.findUndoButtonTime();
                }
                const { taggedUsers, sharedProfiles } = sortPlaceholdersList(this.message?.placeHoldersList);
                this.taggedUsersList = taggedUsers;
                this.sharedProfilesList = sharedProfiles;
            },
            deep: true
        },
        async "message.assetIds"(newVal, oldVal) {
            await this.renderAsset(this.activeConversation.id, this.message.id, newVal);
        }
    },

    data() {
        return {
            formatMessageTime,
            markdownToHtml,
            sanitizeMarkup,
            truncateString,
            fetchedAssets: [],
            _messageService: null as MessageService,
            _navigationService: null as NavigationService,
            dialogRef: ref(null),
            _permissionManager: null as PermissionManager,
            assetLoading: false as boolean,
            showEditMsgBox: false as boolean,
            sharedProfilesList: [] as IMessagePlaceholder[],
            taggedUsersList: [] as IMessagePlaceholder[],
            replyMsgProfiles: [] as IMessagePlaceholder[],
            messageAssets: [],
            editedMsgDetails: {} as any,
            showUndoButton: false as boolean,
            timeoutId: null,
            PlaceholdersListEnum: PlaceholdersListEnum,
            isImportant: false as boolean,
            newMessage: "" as string,
            initialEditMsg: {} as any,
            userOs: "" as string,
            defaultQuickReactions: ["👍", "❤️", "😂", "😮"], // 4 default emojis,
            ConversationTypeEnum,
            showReadCount: false,
            showReadUsers: false,
            hasTestAgency: false
        };
    },

    computed: {
        ...mapState(useApp, ["loggedInUserDetails"]),
        ...mapState(useConversationStore, { activeConversation: "getActiveConversation" }),
        ...mapState(useEditorStore, ["editorMessage"]),

        shouldHideMessageInfo(): boolean {
            if (!this.previousMessage) return false;
            if (this.message.isAutoGenerated || this.message.isDeprecated) return false;
            if (!this.previousMessage.isFromSelf) return false;

            const prevCreated = this.previousMessage.createdOn;
            const currCreated = this.message.createdOn;
            if (!prevCreated || !currCreated) return false;
            const prevTime = moment(prevCreated).format("YYYY-MM-DD HH:mm");
            const currTime = moment(currCreated).format("YYYY-MM-DD HH:mm");

            return prevTime === currTime;
        }
    },
    provide() {
        return {
            dialogRef: computed(() => this.dialogRef.value)
        };
    },
    methods: {
        ...mapActions(useEditorStore, ["setEditorMessage"]),
        async toggleImportant(value: boolean) {
            this.isImportant = value;
        },
        toggleReadCount() {
            this.showReadCount = !this.showReadCount;
        },

        toggleReadUsers() {
            this.showReadUsers = !this.showReadUsers;
        },

        handleOutsideReadClick(event: MouseEvent) {
            const target = event.target as HTMLElement;

            const readStatus = target.closest(".message-status");
            if (!readStatus) {
                this.showReadCount = false;
                this.showReadUsers = false;
            }
        },
        formatReplyToMsgText(message: IReplyToMessage) {
            const { text } = formatTaggedUserMessage(
                message.text,
                message.placeHoldersList,
                this.loggedInUserDetails.loginId
            );
            return text;
        },

        formatMsgText() {
            // If message has tagged users, replace placeholders with usernames
            if (this.taggedUsersList?.length > 0) {
                let newtext = this.message.text;
                this.taggedUsersList.forEach((user) => {
                    newtext = newtext.replace(
                        "%{TAGGED_USER}%",
                        `<span contenteditable='false' id='${user.id}' class='fw-semibold'>${user.name}</span>`
                    );
                });
                return newtext;
            }
            return this.message.text;
        },

        setRepliedMsgProfiles() {
            const { sharedProfiles } = sortPlaceholdersList(this.message.replyToMessage.placeHoldersList);
            this.replyMsgProfiles = sharedProfiles;
        },

        async getProfileRedirectURL(profile: any, event: any) {
            event.stopPropagation();
            let url = "";
            const isClinician = this._permissionManager.checkPermission(
                ParentPermissionEnum.clinician,
                PermissionActionEnum.canView
            );
            if (profile.type === 2) {
                if (profile.applicationId === ApplicationEnum.AxxessHospiceFE) {
                    url = `${getHospiceRedirectionUrl(profile.agencyId)}/employees/employee-center/${profile.id}?accountId=${profile.agencyId}`;
                } else if (profile.applicationId === ApplicationEnum.AxxessPalliative) {
                    url = `${config.palliativeUrl}/employees/employee-center/${profile.id}?accountId=${profile.agencyId}`;
                } else {
                    try {
                        const redirectResponse = await this._navigationService.navigateToEmployee({
                            agencyId: profile.agencyId,
                            applicationId: profile.applicationId,
                            isClinician: isClinician,
                            employeeId: profile.id
                        });
                        url = redirectResponse.redirectUrl;
                    } catch (error: any) {
                        if (error?.status === 403) {
                            const model = {
                                title: `Access Denied`,
                                message:
                                    "You cannot access the Employee Profile as you don't have access to the agency.",
                                buttonText: "Close"
                            };
                            this.dialogRef = this.$dialog.open(Alert, {
                                props: {
                                    modal: true,
                                    showHeader: false,
                                    contentStyle: {
                                        width: "300px",
                                        overflowX: "hidden"
                                    }
                                },
                                data: { model }
                            });
                            return;
                        } else {
                            console.error("Unexpected error:", error);
                        }
                    }
                }
            } else {
                if (profile.applicationId === ApplicationEnum.AxxessHospiceFE) {
                    url = `${getHospiceRedirectionUrl(profile.agencyId)}/patients/patient-chart/${profile.id}?accountId=${profile.agencyId}`;
                } else {
                    try {
                        const redirectResponse = await this._navigationService.navigateToPatient({
                            agencyId: profile.agencyId,
                            applicationId: profile.applicationId,
                            isClinician: isClinician,
                            patientId: profile.id
                        });
                        url = redirectResponse.redirectUrl;
                    } catch (error: any) {
                        if (error?.status === 403) {
                            const model = {
                                title: `Access Denied`,
                                message:
                                    "You cannot access the Patient Profile as you don't have access to the agency.",
                                buttonText: "Close"
                            };
                            this.dialogRef = this.$dialog.open(Alert, {
                                props: {
                                    modal: true,
                                    showHeader: false,
                                    contentStyle: {
                                        width: "300px",
                                        overflowX: "hidden"
                                    }
                                },
                                data: { model }
                            });
                            return;
                        } else {
                            console.error("Unexpected error:", error);
                        }
                    }
                }
            }
            window.open(url, "_blank");
        },
        editMessage() {
            this.showEditMsgBox = true;
            const files = this.fetchedAssets.map((assets: any) => ({
                name: assets.asset.fileName,
                type: assets.asset.mimeType,
                id: assets.asset.id
            }));
            let newtext = this.message.text;
            if (this.taggedUsersList?.length > 0) {
                this.taggedUsersList.forEach((user) => {
                    newtext = newtext.replace(
                        "%{TAGGED_USER}%",
                        `<span><span id='${user.id}' data-actiontype='tagged-user' contenteditable='false' class="blue-text">${user.name}</span><span>&nbsp;</span></span>`
                    );
                });
            }
            this.initialEditMsg = { ...this.message, text: newtext, files };
        },

        closeEditMsgDialog(value: boolean) {
            let promptOptions: IPromptOptions = {
                message: "Do you want to discard this draft?",
                okText: "Discard",
                cancelText: "Cancel"
            };
            this.dialogRef = this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    contentClass: "discard-draft",
                    contentStyle: {
                        overflowX: "hidden"
                    },
                    showHeader: false
                },
                data: {
                    promptOptions
                },
                onClose: async (options) => {
                    if (options.data === "success") {
                        this.showEditMsgBox = false;
                    }
                }
            });
        },
        async sendEditedMsg(value?: any) {
            const message = value.message;
            const msgFiles = value.files;
            const msgProfiles = value.placeHoldersList;
            const assetIds = [];
            eventBus.emit("convWrapperLoader", true);
            // upload new files added
            let assetDetails: IAssetDetails[] = [];
            if (msgFiles.length > 0) {
                let assets: IAsset[] = [];
                if (msgFiles?.length > 25) {
                    msgFiles.splice(25);
                }

                for (const file of msgFiles) {
                    if (file?.size) {
                        assets.push({
                            fileName: "",
                            url: "",
                            mimeType: "",
                            file: file
                        });
                    } else {
                        assetIds.push(file.id);
                    }
                }

                try {
                    let files = assets.map((asset) => asset.file);
                    if (!!files?.length) {
                        assetDetails = await this._messageService.uploadMessageFiles(files as unknown as File[]);
                    }
                } catch (e) {
                    console.error(e);
                }
            }

            try {
                const payload: IEditMessageQuery = {
                    message: message,
                    placeHoldersList: msgProfiles,
                    assetIds,
                    isImportant: this.isImportant,
                    newAssets: assetDetails
                };
                await this._messageService.editMessage(this.activeConversation.id, this.message.id, payload);
                this.showEditMsgBox = false;
                eventBus.emit("convWrapperLoader", false);
            } catch (error) {
                console.error(error);
            }
        },
        async deleteMessage() {
            await this._messageService.deleteMessage(this.activeConversation.id, this.message.id);
        },
        async undoDeletedMsg() {
            await this._messageService.undoDeletedMessage(this.activeConversation.id, this.message.id);
        },
        findUndoButtonTime() {
            const date = new Date(this.message.modifiedOn);
            const timeZoneOffset = date.getTimezoneOffset() * 60 * 1000;
            const localDate = new Date(date.getTime() - timeZoneOffset);
            const millisecDifference = Math.abs(new Date().getTime() - localDate.getTime());

            let timeLeft;
            if (millisecDifference < 30000) {
                // update the timer if user moves to different conv or refreshes the page
                timeLeft = 30000 - millisecDifference;
                this.showUndoButton = true;
                this.timeoutId = setTimeout(() => {
                    this.showUndoButton = false;
                }, timeLeft);
            } else {
                this.showUndoButton = false;
            }
        },

        replyToMessage() {
            this.setEditorMessage({
                ...this.editorMessage,
                replyMessage: { ...this.message, text: this.formatMsgText() }
            });
        },
        async renderAsset(conversationId: string, messageId: string, assetIds: string[]) {
            try {
                this.assetLoading = true;
                const assets = await this._messageService.getAssets(conversationId, messageId, assetIds);
                this.fetchedAssets = assets.map((asset: any) => ({ ...asset, showDownloadBtn: false }));
            } catch (error) {
                console.error(error);
            } finally {
                this.assetLoading = false;
            }
        },
        showImageOverlay(index: number, value: boolean, event: Event) {
            event.stopPropagation();
            this.fetchedAssets[index].asset.showDownloadBtn = value;
        },
        async downloadAsset(asset: any, event: Event) {
            event.preventDefault();
            event.stopPropagation();
            asset.showDownloadBtn = false;
            try {
                const response = await fetch(asset.url);
                if (!response.ok) {
                    throw new Error(`Download failed: ${response.statusText}`);
                }
                const blob = await response.blob();
                const downloadUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.download = asset.fileName || "download";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(downloadUrl);
            } catch (error) {
                console.error("Error downloading file:", error);
                asset.showDownloadBtn = true;
            }
        },
        isImage(mimeType: string): boolean {
            return mimeType.startsWith("image/");
        },
        formatFileSize(size: number) {
            const units = ["B", "KB", "MB", "GB", "TB"];
            let unitIndex = 0;
            let formattedSize = size;
            while (formattedSize > 1024 && unitIndex < units.length - 1) {
                formattedSize /= 1024;
                unitIndex++;
            }
            return `${formattedSize.toFixed(2)} ${units[unitIndex]}`;
        },
        handleReplyMsgNavigation(event: Event) {
            event.stopPropagation();
            eventBus.emit("navigateToRepliedMsg", this.message.replyToMessage.messageId);
        },
        closeEmojiPicker() {
            const picker = this.$refs.emojiPicker as any;
            if (picker?.showPicker) {
                picker.showPicker = false;
            }
        },
        async handleEmoji(emoji: string) {
            (this.$refs.hoverActions as any)?.close();

            try {
                await this._messageService.sendReaction(this.message.id, this.activeConversation.id, emoji);
            } catch (e) {
                console.error(e);
            }
        }
    },
    unmounted() {
        clearTimeout(this.timeoutId);
    }
});
